import { IconButton } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import CloseIcon from '@material-ui/icons/Close';
import deburr from 'lodash/deburr';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import TextField from '../TextField';
import { ISelectOption, ISelectPaperProps, ISelectProps, ISelectRefactorProps } from './interfaces';
import AutoComplete, { AutocompleteProps } from '@material-ui/lab/Autocomplete';
import './styles.scss';

const SelectPaper: React.FC<ISelectPaperProps> = ({
  isOpen,
  inputValue,
  className,
  render,
  search,
  setPaperOpened,
  paperStyle,
  searchText,
  filter,
  ...rest
}) => {
  const isSearch = typeof search !== 'undefined' && search !== null;
  // let timeOut = useRef<NodeJS.Timeout | null>(null);

  const [options] = useState<any[]>([...rest.options]);
  const [items, setItems] = useState<ISelectOption[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const paperRef = useRef<any | undefined>();
  const observer = useRef<IntersectionObserver | undefined>();
  const lastItemElement = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevStatePage) => prevStatePage + 1);
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [loading, hasMore],
  );

  const getItems = useCallback((): void => {
    const searchValue = inputValue === null ? '' : deburr(inputValue.trim()).toLowerCase();
    let count = 0;

    const filteredItems = options.filter((option: ISelectOption) => {
      const condition = !!filter ? filter(option, searchValue) : option.label.toLowerCase().indexOf(searchValue) > -1;
      const keep = count < 15 * page && condition;

      if (keep) {
        count += 1;
      }

      return keep;
    });

    setItems(filteredItems);
    setHasMore(count === filteredItems.length);
    setLoading(false);

    if (page === 1 && paperRef.current) {
      paperRef.current.scrollTo(0, 0);
    }
  }, [inputValue, options, page, filter]);

  const optionsChange = useCallback(() => {
    if (!search) {
      if (items.length < 1 && options.length > 0) {
        getItems();
      }
    }
  }, [search, items.length, options.length, getItems]);

  useEffect(optionsChange, [options]);

  const inputValueChange = useCallback(() => {
    if (isSearch) {
    } else {
      setItems([]);
      setPage(1);
      getItems();
    }
  }, [isSearch, getItems]);

  useEffect(inputValueChange, [inputValue]);

  const pageChange = useCallback(() => {
    if (!loading) {
      setItems([]);
      getItems();
    }
  }, [loading, getItems]);

  useEffect(pageChange, [page]);

  const restOptionsChange = useCallback(() => {
    if (!!search) {
      setItems([...rest.options]);
    }
  }, [search, rest.options]);

  useEffect(restOptionsChange, [rest.options]);

  const itemsLength = items.length;

  if (isSearch && itemsLength < 1) return null;

  if (!isOpen || !itemsLength) {
    return null;
  }

  return (
    <Paper className={className} style={paperStyle ? paperStyle : {}} ref={paperRef}>
      {items.map((item, index: number) => {
        if (itemsLength === index + 1) {
          return (
            <div ref={lastItemElement} key={index.toString()}>
              {render(item, index)}
            </div>
          );
        }

        return <div key={index.toString()}>{render(item, index)}</div>;
      })}
    </Paper>
  );
};

const SelectNew: React.FC<ISelectProps> = forwardRef((props: ISelectProps, ref) => {
  const {
    initialSelectedItem,
    initialInputValue,
    onChange,
    search,
    value,
    onFocus,
    onBlur,
    InputProps = {},
    onKeyDown,
    renderItem: propsRenderItem,
    filter,
    options: propsOptions,
    ...rest
  } = props;
  const [options, setOptions] = useState<ISelectOption[]>(propsOptions);
  const [searchText, setSearchText] = useState<string>(initialInputValue || '');
  const [selectedItem, setSelectedItem] = useState<ISelectOption | null>(value || null);
  const [paperOpened, setPaperOpened] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const timeout = useRef<any>(null);

  const valueChange = useCallback(() => {
    if (value && selectedItem && value.value !== selectedItem.value) {
      setSelectedItem(value);
    }
  }, [value, selectedItem]);

  useEffect(valueChange, [value]);

  const changePaperVisibilityState = (state: boolean): void => {
    setPaperOpened(state);
  };

  const onChangeSearchText = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedItem) {
      setSelectedItem(null);
      onChange(null, 0);
    } else {
      setSearchText(e.target.value);
    }
  };

  const onTextFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (selectedItem) {
      if (e.keyCode === 8 || e.keyCode === 27) {
        setSearchText('');
      } else {
        setSearchText(e.key);
      }
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const onTextFieldKeyUp = () => {
    if (!!search && searchText.trim()) {
      // e.keyCode === 13 add this condition for searching on "Enter"
      setPaperOpened(false);
      setOptions([]);

      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      timeout.current = setTimeout(() => {
        // Cancel request if user continue writing
        setLoading(true);
        search(searchText)
          .then((response) => {
            setOptions(response);
            setLoading(false);
            setPaperOpened(true);
          })
          .catch(() => {
            setOptions([]);
            setLoading(false);
            setPaperOpened(true);
          });
      }, 500);
    }
  };

  const onClickMenuItem = (option: ISelectOption, index: number) => () => {
    setSelectedItem({ ...option });
    onChange({ ...option }, index);
    setPaperOpened(false);
  };

  const renderItem = (option: ISelectOption, index: number): React.ReactNode => {
    if (!!propsRenderItem) {
      return (
        <MenuItem component="div" style={{ overflowWrap: 'unset' }} onClick={onClickMenuItem(option, index)}>
          {propsRenderItem(option)}
        </MenuItem>
      );
    }

    return (
      <MenuItem component="div" style={{ overflowWrap: 'unset' }} onClick={onClickMenuItem(option, index)}>
        <p style={{ margin: 0, overflowWrap: 'break-word', width: '100%', whiteSpace: 'pre-line' }}>{option.label}</p>
      </MenuItem>
    );
  };

  const onSearchTextFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // if (!search) {
    //   setPaperOpened(true);
    // }
    setPaperOpened(true);

    if (onFocus) {
      onFocus(e);
    }
  };

  const onSearchTextFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      // onBlur(e);
    }
  };

  const onClickAway = () => {
    // if (paperOpened) {
    //   setPaperOpened(false);
    // }
    setPaperOpened(false);

    if (!selectedItem) {
      setSearchText('');
    }
  };

  const onClickClear = () => {
    setSelectedItem(null);
    onChange(null);
    setSearchText('');
    setOptions([]);

    if (paperOpened) {
      setPaperOpened(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div className="select">
        <TextField
          {...rest}
          autoComplete="off"
          disabled={rest.disabled}
          InputProps={{
            ...InputProps,
            endAdornment: (
              <React.Fragment>
                {loading && (
                  <InputAdornment position="end">
                    <IconButton size="small" disabled>
                      <CircularProgress color="primary" size={20} style={{ position: 'initial' }} />
                    </IconButton>
                  </InputAdornment>
                )}
                {selectedItem && selectedItem!.value !== '' && (
                  <IconButton size="small" onClick={onClickClear}>
                    <CloseIcon style={{ opacity: 0.5 }} />
                  </IconButton>
                )}
                <IconButton size="small" onClick={() => setPaperOpened((prevPaperOpened) => !prevPaperOpened)}>
                  {!paperOpened ? (
                    <ArrowDropDownIcon style={{ opacity: 0.5 }} />
                  ) : (
                    <ArrowDropUpIcon style={{ opacity: 0.5 }} />
                  )}
                </IconButton>
              </React.Fragment>
            ),
          }}
          onKeyUp={onTextFieldKeyUp}
          onKeyDown={onTextFieldKeyDown}
          onBlur={onSearchTextFieldBlur}
          onFocus={onSearchTextFieldFocus}
          onChange={onChangeSearchText}
          fullWidth
          label={props.label}
          value={selectedItem ? selectedItem!.label : searchText}
        />
        {!rest.disabled && (
          <SelectPaper
            isOpen={paperOpened}
            paperStyle={props.paperStyle}
            setPaperOpened={changePaperVisibilityState}
            filter={filter}
            search={search}
            searchText={searchText}
            options={options}
            render={renderItem}
            className="select__paper"
            inputValue={searchText}
          />
        )}
      </div>
    </ClickAwayListener>
  );
});

interface IAutoCompletePropsCustom extends AutocompleteProps<any, any, any, any> {
  id?: string;
}

export const SelectRefactor: React.FC<ISelectRefactorProps> = ({
  renderInput,
  loading,
  onBlur,
  label,
  onChange,
  optionDefaultLabel,
  error,
  helperText,
  searchByText,
  ...rest
}) => {
  //Local data
  const [inputValue, setInputValue] = useState<string>('');

  //Methods
  const onInputKeyUp = () => {
    if (searchByText) {
      searchByText(inputValue);
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const renderAutoCompleteInput = (params: any): React.ReactNode => {
    const { InputProps, ...restParams } = params;

    return (
      <TextField
        {...restParams}
        fullWidth
        error={error}
        autoComplete="off"
        InputProps={{
          ...InputProps,
          endAdornment: (
            <React.Fragment>
              {loading ? (
                <InputAdornment position="end">
                  <div className="MuiAutocomplete-endAdornment">
                    <IconButton size="small" disabled>
                      <CircularProgress style={{ position: 'relative', top: 0 }} color="primary" size={20} />
                    </IconButton>
                  </div>
                </InputAdornment>
              ) : (
                InputProps.endAdornment
              )}
            </React.Fragment>
          ),
        }}
        helperText={helperText}
        onBlur={onBlur}
        label={label}
        onKeyUp={onInputKeyUp}
        onChange={onInputChange}
        onKeyDown={(event: any) => {
          if (event.key === 'Backspace') {
            event.stopPropagation();
          }
        }}
      />
    );
  };

  const onChangeOption = (e: any, option: any): void => {
    if (onChange) {
      onChange(option);
    }
  };

  const autoCompleteProps: IAutoCompletePropsCustom = {
    ...rest,
    noOptionsText: rest.noOptionsText
      ? rest.noOptionsText
      : rest.options && rest.options.length > 0
      ? 'Нет совпадений'
      : 'Нет данных',
    onChange: onChangeOption,
    renderInput: renderAutoCompleteInput,
  };

  if (label === 'Исполнитель заявки') {
    autoCompleteProps.id = 'data-test-executor';
  }

  if (label === 'Статус') {
    autoCompleteProps.id = 'data-test-status';
  }

  if (rest.renderOption) {
    autoCompleteProps.renderOption = rest.renderOption;
  }

  return <AutoComplete {...autoCompleteProps} />;
};

export default SelectNew;
